import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold text-white" data-testid="forgot-password-title">Reset Password</h1>
          <p className="text-slate-400 text-sm mt-2">
            {sent ? "Check your inbox" : "Enter your admin email to receive a reset link"}
          </p>
        </div>

        {sent ? (
          <div className="bg-slate-900 rounded-2xl p-6 sm:p-8 border border-slate-800 text-center" data-testid="reset-sent-confirmation">
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="text-white font-semibold mb-2">If an account exists for that email,</p>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              a password reset link has been sent. The link expires in 30 minutes.
              Check your spam folder if you don't see it within a few minutes.
            </p>
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-semibold"
              data-testid="back-to-login-link"
            >
              ← Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-slate-900 rounded-2xl p-6 sm:p-8 border border-slate-800 space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label className="text-slate-400 text-sm mb-2 block">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@culturealign.com"
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                data-testid="forgot-email-input"
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-button"
              className="w-full bg-white text-slate-950 hover:bg-slate-100 font-semibold py-5"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
              {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>

            <p className="text-center text-slate-500 text-xs pt-2">
              <Link to="/admin/login" className="hover:text-cyan-400 transition-colors">← Back to login</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
